import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@ui/Button';
import { FloatingShapes } from '@ui/FloatingShapes';
import { useAuthStore } from '@stores/authStore';
import { authService } from '@services/authService';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError } = useAuthStore();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const from = location.state?.from?.pathname || '/main';

  useEffect(() => {
    clearError();
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailNotVerified(false);
    setResendStatus('idle');

    try {
      await login(formData.username, formData.password);
      navigate(from, { replace: true });
    } catch (err: any) {
      const errorData = err.response?.data?.errors;
      if (errorData?.email_not_verified) {
        setEmailNotVerified(true);
        setUnverifiedEmail(errorData.email || '');
      }
      console.error('Login error:', err);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    setResendStatus('sending');
    try {
      await authService.resendVerification(unverifiedEmail);
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-700 p-4 relative overflow-hidden">
      <FloatingShapes />

      <div className="w-full max-w-md relative z-10">
        {/* 品牌区域 */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 bg-white rounded-2xl flex items-center justify-center shadow-lg">
            <img src="/logo.png" alt="Logo" className="w-12 h-12" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">少儿AI编程学习平台</h1>
          <p className="text-blue-100 text-sm">AI项目创作，让学习变得有趣</p>
        </div>

        {/* 登录卡片 */}
        <div className="bg-white rounded-2xl p-8 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {emailNotVerified && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800 mb-3">
                  邮箱尚未验证，请查收验证邮件。
                </p>
                {resendStatus === 'sent' ? (
                  <p className="text-sm text-green-600">验证邮件已重新发送，请查收。</p>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleResendVerification}
                    disabled={resendStatus === 'sending'}
                  >
                    {resendStatus === 'sending' ? '发送中...' : '重新发送验证邮件'}
                  </Button>
                )}
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                用户名
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="请输入用户名"
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                密码
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="请输入密码"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={isLoading}
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  记住我
                </label>
              </div>

              <div className="text-sm">
                <a href="/forgot-password" className="font-medium text-blue-600 hover:text-blue-500">
                  忘记密码？
                </a>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full py-3"
              loading={isLoading}
            >
              {isLoading ? '登录中...' : '登录'}
            </Button>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                还没有账户？{' '}
                <a href="/register" className="font-medium text-blue-600 hover:text-blue-500">
                  立即注册
                </a>
              </p>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">或</span>
              </div>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={async () => {
                  try {
                    const res = await authService.getQQLoginUrl();
                    window.location.href = res.authorize_url;
                  } catch (err) {
                    console.error('Failed to get QQ login URL:', err);
                  }
                }}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 font-medium"
                disabled={isLoading}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8" r="5" fill="#12B7F5" />
                  <path d="M7.5 13c-2.5 1-4.5 3.5-4.5 5.5 0 .5.5 1.5 2 1.5h14c1.5 0 2-1 2-1.5 0-2-2-4.5-4.5-5.5" fill="#12B7F5" />
                  <ellipse cx="12" cy="17" rx="7" ry="3" fill="#12B7F5" />
                  <circle cx="10" cy="7.5" r="1" fill="white" />
                  <circle cx="14" cy="7.5" r="1" fill="white" />
                </svg>
                QQ 登录
              </button>
            </div>

            <div className="text-center">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => window.location.href = '/guest'}
                disabled={isLoading}
              >
                访客模式
              </Button>
            </div>
          </form>
        </div>

        {/* 底部信息 */}
        <div className="text-center mt-6 text-blue-100 text-sm">
          <p>&copy; 2025 少儿AI编程学习平台. 让每个孩子都能创造AI</p>
        </div>
      </div>
    </div>
  );
};
